// DALMobileBlock — Bloc autonome "Accès DAL Mobile"
// Envoie référence + PIN + lien omnihub-ia.fr/mobile/dal au visiteur/client
// via email, SMS ou WhatsApp. Utilisé dans OperationSection sous la carte Visite.

import { useState, useEffect } from "react";
import {
  Smartphone, Send, Mail, MessageSquare, User, UserPlus,
  Loader2, CheckCircle2, ChevronDown
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "../../../components/ui/dialog";
import { API, getAuthHeaders } from "./helpers";
import { toast } from "sonner";
import { formatClientFullName } from "../../../utils/clientFormat";

export function DALMobileBlock({ dossier, dossierId, tenantId, showEmptyState = false }) {
  const [showSendAccess, setShowSendAccess] = useState(false);
  const [sendChannel, setSendChannel] = useState("email");
  const [sendRecipient, setSendRecipient] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [sending, setSending] = useState(false);
  const [visiteurInfo, setVisiteurInfo] = useState(null);

  // Charger les infos du visiteur depuis op_visiteurs pour auto-remplir email/tel
  useEffect(() => {
    if (!dossier.visiteur_amo) return;
    const loadVisiteur = async () => {
      try {
        const hdrs = getAuthHeaders(tenantId);
        const res = await fetch(
          `${API}/api/metier/op-visiteurs?search=${encodeURIComponent(dossier.visiteur_amo)}&include_archived=true`,
          { headers: hdrs, credentials: "include" }
        );
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : data.visiteurs || [];
          if (list.length > 0) setVisiteurInfo(list[0]);
        }
      } catch {}
    };
    loadVisiteur();
  }, [dossier.visiteur_amo, tenantId]);

  // Contacts liés au dossier uniquement
  const dossierContacts = (() => {
    const contacts = [];
    if (dossier.visiteur_amo) {
      contacts.push({
        id: "visiteur", label: dossier.visiteur_amo, role: "Visiteur AMO", icon: "user",
        email: visiteurInfo?.email || "", phone: visiteurInfo?.telephone || "",
      });
    }
    const clientName = dossier.client_nom || formatClientFullName(dossier.client);
    if (clientName) {
      contacts.push({
        id: "client", label: clientName, role: "Client",
        email: dossier.client?.email, phone: dossier.client?.telephone, icon: "home"
      });
    }
    return contacts;
  })();

  const handleSendAccess = async () => {
    if (!sendRecipient.trim()) {
      toast.error("Saisissez un destinataire");
      return;
    }
    setSending(true);
    try {
      const headers = getAuthHeaders(tenantId, "application/json");
      const res = await fetch(`${API}/api/dal/send-access/${dossierId}`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({ channel: sendChannel, recipient: sendRecipient.trim() }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        if (data.method === "link" && data.wa_link) {
          window.open(data.wa_link, "_blank");
          toast.success("Lien WhatsApp ouvert");
        } else {
          toast.success(`Accès envoyé par ${sendChannel === "email" ? "email" : sendChannel === "sms" ? "SMS" : "WhatsApp"}`);
        }
        setShowSendAccess(false);
      } else {
        toast.error(data.detail || data.error || "Erreur d'envoi");
      }
    } catch {
      toast.error("Erreur réseau");
    } finally {
      setSending(false);
    }
  };

  // Pas de visiteur assigné : par défaut on n'affiche rien (usage OperationSection).
  // Dans le split DAL (showEmptyState), on rend un état vide explicite pour que la
  // section « DAL Mobile » ne soit jamais un panneau droit vide (régression split).
  if (!dossier.visiteur_amo) {
    if (!showEmptyState) return null;
    return (
      <div className="rounded-lg overflow-hidden" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }} data-testid="dal-mobile-block">
        <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: "1px solid var(--oh-border)" }}>
          <Smartphone className="w-4 h-4 text-[var(--oh-stat-cyan-text)]" />
          <span className="text-sm font-medium" style={{ color: "var(--oh-text)" }}>DAL Mobile</span>
        </div>
        <div className="px-4 py-4 text-sm" style={{ color: "var(--oh-text-muted)" }}>
          Aucun visiteur n'est assigné à ce dossier. Assignez un <b>visiteur AMO</b> (onglet
          « Demande MPA ») pour pouvoir lui envoyer l'accès DAL Mobile (numéro de dossier, PIN
          et lien d'accès terrain).
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-lg overflow-hidden" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }} data-testid="dal-mobile-block">
        <details>
          <summary className="flex items-center justify-between px-4 py-3 cursor-pointer select-none">
            <span className="text-sm font-medium flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
              <Smartphone className="w-4 h-4 text-[var(--oh-stat-cyan-text)]" />
              DAL Mobile
            </span>
            <ChevronDown className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />
          </summary>
          <div className="px-4 pb-4">
            <div className="flex justify-end mb-2">
              <Button
                size="sm"
                onClick={() => { setShowSendAccess(true); setSelectedContact(null); setSendRecipient(""); setSendChannel("email"); }}
                className="bg-cyan-600 hover:bg-cyan-700 text-[var(--oh-accent-contrast)] text-xs h-8"
                data-testid="btn-send-dal-access"
              >
                <Send className="w-3.5 h-3.5 mr-1.5" /> Envoyer accès visiteur
              </Button>
            </div>
            <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>
              Envoie le numéro de dossier, le PIN et le lien d'accès au visiteur par email, SMS ou WhatsApp.
            </p>
          </div>
        </details>
      </div>

      {/* Dialog envoi accès */}
      <Dialog open={showSendAccess} onOpenChange={setShowSendAccess}>
        <DialogContent className="bg-[var(--oh-bg-surface)] border-white/10 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[var(--oh-text)] flex items-center gap-2">
              <Send className="w-5 h-5 text-[var(--oh-stat-cyan-text)]" /> Envoyer accès DAL Mobile
            </DialogTitle>
            <DialogDescription className="text-[var(--oh-text-secondary)]">
              Envoie au visiteur : numéro de dossier, PIN, lien omnihub-ia.fr/mobile/dal
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Résumé dossier */}
            <div className="bg-[var(--oh-bg-elevated)] rounded-lg p-3 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-[var(--oh-text-secondary)]">Dossier</span>
                <span className="text-[var(--oh-text)] font-mono">{dossier.reference || dossier.référence || "—"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--oh-text-secondary)]">Client</span>
                <span className="text-[var(--oh-text)]">{dossier.client_nom || "—"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--oh-text-secondary)]">Visiteur</span>
                <span className="text-[var(--oh-text)]">{dossier.visiteur_amo || "—"}</span>
              </div>
            </div>

            {/* Choix du canal */}
            <div>
              <label className="text-xs text-[var(--oh-text-secondary)] mb-2 block">Canal d'envoi</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  { key: "email", label: "Email", icon: Mail, color: "blue" },
                  { key: "sms", label: "SMS", icon: MessageSquare, color: "green" },
                ].map(c => (
                  <button
                    key={c.key}
                    onClick={() => {
                      setSendChannel(c.key);
                      if (selectedContact) {
                        const contact = dossierContacts.find(ct => ct.id === selectedContact);
                        if (c.key === "email" && contact?.email) setSendRecipient(contact.email);
                        else if (c.key === "sms" && contact?.phone) setSendRecipient(contact.phone);
                        else setSendRecipient("");
                      } else {
                        setSendRecipient("");
                      }
                    }}
                    className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-all border ${
                      sendChannel === c.key
                        ? `bg-${c.color}-500/20 border-${c.color}-500/50 text-${c.color}-400`
                        : "bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] text-[var(--oh-text-secondary)] hover:border-[var(--oh-border)]"
                    }`}
                    data-testid={`channel-${c.key}`}
                  >
                    <c.icon className="w-4 h-4" />
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Destinataire — filtré sur les contacts du dossier */}
            <div>
              <label className="text-xs text-[var(--oh-text-secondary)] mb-2 block">Destinataire</label>
              <div className="space-y-1.5 mb-3">
                {dossierContacts.map(c => {
                  const isSelected = selectedContact === c.id;
                  return (
                    <button
                      key={c.id}
                      onClick={() => {
                        setSelectedContact(c.id);
                        if (sendChannel === "email" && c.email) setSendRecipient(c.email);
                        else if (sendChannel === "sms" && c.phone) setSendRecipient(c.phone);
                        else setSendRecipient("");
                      }}
                      className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left text-sm transition-all border ${
                        isSelected
                          ? "bg-[var(--oh-stat-cyan-active-bg)] border-[var(--oh-stat-cyan-active-border)] text-[var(--oh-text)]"
                          : "bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] text-[var(--oh-text-secondary)] hover:border-[var(--oh-border)]"
                      }`}
                      data-testid={`contact-${c.id}`}
                    >
                      {c.icon === "user"
                        ? <User className={`w-4 h-4 shrink-0 ${isSelected ? "text-[var(--oh-stat-cyan-text)]" : "text-[var(--oh-text-muted)]"}`} />
                        : <UserPlus className={`w-4 h-4 shrink-0 ${isSelected ? "text-[var(--oh-stat-cyan-text)]" : "text-[var(--oh-text-muted)]"}`} />
                      }
                      <span className="flex-1 truncate">{c.label}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${isSelected ? "bg-[var(--oh-stat-cyan-active-bg)] text-[var(--oh-stat-cyan-text)]" : "bg-[var(--oh-bg-elevated)] text-[var(--oh-text-muted)]"}`}>{c.role}</span>
                      {isSelected && <CheckCircle2 className="w-4 h-4 text-[var(--oh-stat-cyan-text)] shrink-0" />}
                    </button>
                  );
                })}
                {dossierContacts.length === 0 && (
                  <p className="text-xs text-[var(--oh-text-muted)] italic">Aucun contact lié au dossier</p>
                )}
              </div>
            </div>

            {/* Coordonnées du destinataire */}
            <div>
              <label className="text-xs text-[var(--oh-text-secondary)] mb-1.5 block">
                {sendChannel === "email" ? "Adresse email" : "Numéro de téléphone"}
                {selectedContact && <span className="text-[var(--oh-text-muted)] ml-1">({dossierContacts.find(c => c.id === selectedContact)?.label})</span>}
              </label>
              <Input
                data-testid="send-access-recipient"
                type={sendChannel === "email" ? "email" : "tel"}
                placeholder={sendChannel === "email" ? "visiteur@email.com" : "06 12 34 56 78"}
                value={sendRecipient}
                onChange={e => setSendRecipient(e.target.value)}
                className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] text-[var(--oh-text)]"
              />
            </div>

            {/* Bouton envoi */}
            <Button
              className="w-full bg-cyan-600 hover:bg-cyan-700"
              onClick={handleSendAccess}
              disabled={sending || !sendRecipient.trim()}
              data-testid="confirm-send-access"
            >
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {sending ? "Envoi en cours..." : "Envoyer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default DALMobileBlock;
